import { useCallback, useEffect, useRef, useState } from 'react';
import { reviewService } from '@/services';
import { useBottomToastStore } from '@/stores/bottomToastStore';
import { toast } from '@/stores/toastStore';
import { REVIEW_COPY } from './reviewCopy';

/** 8 min: under review_undo_window() (10 min) with margin for timer throttling and the RPC round trip before arm(). */
export const REVIEW_UNDO_TOAST_MAX_MS = 8 * 60_000;

export type UndoableAction = {
  batchId: string;
  label: string;
  /** Puts the card back on screen once the server has reverted the batch. */
  restore(): void;
};

export interface UseReviewUndoResult {
  /** Raise the Undo toast for the latest action; replaces any earlier one (only the latest is undoable). */
  arm(action: UndoableAction): void;
  undoing: boolean;
}

/**
 * Review v0 Undo (YAR-552): one action toast for the latest card action.
 *
 * The toast is the shared bottom slot's action lane, so it shows for the
 * production 6 s of VISIBLE time (paused on hover / keyboard focus / an open
 * sheet). That clock can be held indefinitely, so the toast also carries a
 * hard wall-clock cap, {@link REVIEW_UNDO_TOAST_MAX_MS} (8 min), which the
 * slot enforces even while paused: the toast is gone before the server's
 * `review_undo_window()` (10 min) closes, so Undo is never offered when it
 * can't work (YAR-552 AC4, YAR-555 AC14). The 2 min margin covers timer
 * throttling in a background tab and the RPC round trip before arm().
 *
 * There is no "too late" message. Should a tap still land after the server
 * window, `undo_window_closed` is silent: the parent refetches the list.
 */
export function useReviewUndo(opts: {
  onUndone(): void;
  onUndoFailed(err: unknown): void;
}): UseReviewUndoResult {
  const [undoing, setUndoing] = useState(false);
  const toastIdRef = useRef<string | null>(null);
  // One Undo RPC at a time, whatever the slot does with repeated taps.
  const inFlightRef = useRef(false);
  // Callbacks change every render; the toast's onAction reads the latest.
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  });

  const undo = useCallback(async (action: UndoableAction) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setUndoing(true);
    try {
      await reviewService.undoReviewBatch(action.batchId);
      action.restore();
      optsRef.current.onUndone();
    } catch (err) {
      if (!(err instanceof reviewService.UndoWindowClosedError)) {
        toast.error(REVIEW_COPY.failHeadline);
      }
      optsRef.current.onUndoFailed(err);
    } finally {
      inFlightRef.current = false;
      setUndoing(false);
    }
  }, []);

  const arm = useCallback(
    (action: UndoableAction) => {
      // showAction ends any earlier toast ('replaced'): its Undo is gone.
      // Each action is undone at most once.
      let used = false;
      const id = useBottomToastStore.getState().showAction({
        name: action.label,
        suffix: '',
        actionLabel: REVIEW_COPY.undo,
        expiresAt: Date.now() + REVIEW_UNDO_TOAST_MAX_MS,
        onAction: () => {
          if (used || inFlightRef.current) return;
          used = true;
          void undo(action);
        },
        onEnd: () => {
          if (toastIdRef.current === id) toastIdRef.current = null;
        },
      });
      toastIdRef.current = id;
    },
    [undo],
  );

  // Leaving the page takes the toast down with it (the slot also ends it on
  // a route change; this covers an unmount on the same path).
  useEffect(
    () => () => {
      const id = toastIdRef.current;
      if (id) useBottomToastStore.getState().dismissAction(id, 'dismissed');
    },
    [],
  );

  return { arm, undoing };
}
